package com.swetdot.swetgram.data.api

import com.swetdot.swetgram.protocol.dto.ErrorCodes
import com.swetdot.swetgram.protocol.dto.ValidationErrorItem

/**
 * Every failure the API layer can produce, as a closed set the UI can exhaustively handle.
 *
 * Modelled on `ERRORS.md`: the client localizes from `code`, and the server's `message` is only
 * an English fallback that must never be shown when a localized string exists.
 *
 * Several codes carry obligations the caller cannot infer from an HTTP status alone, so they
 * get their own cases rather than collapsing into a generic HttpError:
 *
 *  - [UpgradeRequired] must block the app and prompt for an update; retrying is pointless.
 *  - [SessionExpired] must clear the session and return to sign-in -- for
 *    `refresh_token_reuse_detected` the server has already revoked everything, so trying to
 *    refresh again would loop forever.
 *  - [RateLimited] carries the server's own retry delay; guessing one makes the limit worse.
 *  - [VersionConflict] means reload before rewriting, never blind retry.
 */
// The cause travels through the constructor rather than `initCause`, which is JVM-only and
// does not exist on Kotlin/Native -- it compiles for Android and fails the iOS build.
sealed class ApiError(message: String, cause: Throwable? = null) : Exception(message, cause) {

    /** No usable network. Distinct from a server error because retrying is reasonable. */
    class Offline(cause: Throwable?) : ApiError("No network connection", cause)

    /** The request did not complete inside the contract's 15-second budget. */
    class Timeout(cause: Throwable?) : ApiError("Request timed out", cause)

    class Validation(
        val code: String,
        val errors: List<ValidationErrorItem>,
        message: String,
    ) : ApiError(message)

    /** 401. The access token is missing, expired or rejected -- a refresh may still help. */
    class Unauthorized(val code: String, message: String) : ApiError(message)

    /**
     * The session itself is gone: revoked, or a rotated refresh token was replayed. Refreshing
     * cannot recover it; the only correct response is to sign out.
     */
    class SessionExpired(val code: String, message: String) : ApiError(message)

    /**
     * A moderator has suspended this account.
     *
     * Its own case rather than an [Unauthorized] carrying a code, because the two need opposite
     * handling and the difference is invisible from the status. A plain 401 means "refresh and try
     * again"; this one means the token is fine and will never work, so retrying and refreshing both
     * loop. Left as an `Unauthorized` the app kept a session it could not use and answered every
     * screen with a generic failure, never signing out and never saying why.
     *
     * Handled like [SessionExpired] -- sign out, clear the cache, return to sign-in -- and the
     * reason then arrives again on the sign-in attempt, where there is somewhere to show it.
     */
    class AccountSuspended(message: String) : ApiError(message)

    class Forbidden(val code: String, message: String) : ApiError(message)

    class NotFound(val code: String, message: String) : ApiError(message)

    class Conflict(val code: String, message: String) : ApiError(message)

    class VersionConflict(message: String) : ApiError(message)

    class RateLimited(val retryAfterSeconds: Long?, message: String) : ApiError(message)

    /** 426. The installed build is below the supported minimum. */
    class UpgradeRequired(message: String) : ApiError(message)

    /** 5xx, or anything the client does not recognise. */
    class Server(val status: Int, val code: String?, message: String) : ApiError(message)

    val isRetryable: Boolean
        get() = when (this) {
            is Offline, is Timeout -> true
            is Server -> status >= 500
            is RateLimited -> true
            else -> false
        }

    companion object {
        /** Maps the contract's error codes onto the cases above. */
        fun fromCode(status: Int, code: String, message: String, errors: List<ValidationErrorItem>): ApiError =
            when (code) {
                ErrorCodes.CLIENT_UPGRADE_REQUIRED -> UpgradeRequired(message)
                ErrorCodes.SESSION_REVOKED,
                ErrorCodes.REFRESH_TOKEN_REUSE_DETECTED,
                -> SessionExpired(code, message)
                ErrorCodes.ACCOUNT_SUSPENDED -> AccountSuspended(message)
                ErrorCodes.UNAUTHORIZED, ErrorCodes.INVALID_CREDENTIALS -> Unauthorized(code, message)
                ErrorCodes.VALIDATION_FAILED -> Validation(code, errors, message)
                ErrorCodes.VERSION_CONFLICT -> VersionConflict(message)
                ErrorCodes.RATE_LIMIT_EXCEEDED -> RateLimited(null, message)
                else -> when (status) {
                    400 -> Validation(code, errors, message)
                    401 -> Unauthorized(code, message)
                    403 -> Forbidden(code, message)
                    404 -> NotFound(code, message)
                    409 -> Conflict(code, message)
                    426 -> UpgradeRequired(message)
                    429 -> RateLimited(null, message)
                    else -> Server(status, code, message)
                }
            }
    }
}
